/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import MenuItem from "@mui/material/MenuItem";
import { numberFieldDefault, requiredValidator } from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import NumberFieldConfigured from "../ConfiguredFormElements/NumberFieldConfigured/NumberFieldConfigured";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";

type RealEstateProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function RealEstate({ submitProps, product }: RealEstateProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Vermögen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasRealEstate"}
                question={"Besitzen Sie Grundeigentum?"}
                modalContent={
                  <p>
                    Hierzu zählen Grundstücke, Häuser, Eigentumswohnungen und Erbbaurechte. Auch wenn Sie selbst genutzte Immobilien haben, kann Ihnen
                    Verfahrenskostenhilfe bewilligt werden. Eine Angabe ist daher immer erforderlich.
                  </p>
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasRealEstate === "yes" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>Bitte geben Sie hier weitere Details zu Ihrem Grundeigentum an.</FormLegend>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="realEstate1Size"
                    label="Größe in Quadratmetern"
                    validators={[requiredValidator("Bitte geben Sie die Größe in Quadratmetern an"), numberFieldDefault]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="realEstate1Location"
                    label="Anschrift/Grundbuchbezeichnung"
                    validators={[requiredValidator("Bitte geben Sie die Anschrift/Grundbuchbezeichnung an")]}
                  />
                </Grid>
                <Grid size={12}>
                  <ValidatorSelectField
                    label="Allein- oder Miteigentum"
                    name="realEstate1Ownership"
                    validators={[requiredValidator("Bitte geben Sie die Besitzverhältnisse an")]}
                    MenuProps={{ slotProps: { paper: { style: { maxHeight: 200 } } } }}
                  >
                    <MenuItem value={"Alleineigentum"}>Alleineigentum</MenuItem>
                    <MenuItem value={"Miteigentum"}>Miteigentum</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid size={12}>
                  <ValidatorTextField
                    name="realEstate1NumberApartments"
                    label="Zahl der Wohneinheiten"
                    validators={[requiredValidator("Bitte geben Sie die Zahl der Wohneinheiten an"), numberFieldDefault]}
                  />
                </Grid>
                <NumberFieldConfigured caseData={product} fieldPath={"realEstate1Value"} required numberType={"float"} fieldType={"money"} />
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
